"use client";

import { Fragment } from "react";
import Link from "next/link";
import { ArrowRight, ShieldCheck } from "lucide-react";
import ConsultationCTA from "@/components/services/ConsultationCTA";
import StructuredData from "@/components/common/StructuredData";
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n";
import { getClaudePrivacyComplianceContent } from "@/content/claude-privacy-compliance-i18n";
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

// Privacy/compliance details compiled from Anthropic's Privacy Center
// (privacy.claude.com), Trust Center (trust.anthropic.com), Help Center
// (support.claude.com), and product pages. Accurate at July 2026 — reference
// only, verify against Anthropic before relying on it. Anchor Sprint is a
// Certified in the Claude Partner Network (deployment/rollout), not a
// reseller, and this is not legal advice.

export default function ClaudePrivacyCompliance({
  locale = defaultLocale,
}: {
  locale?: Locale;
}) {
  const c = getClaudePrivacyComplianceContent(locale);

  return (
    <div className="bg-background">
      {/* Shared with the on-page accordion below so the FAQPage schema and the
          accordion never drift. */}
      <StructuredData type="faq" faqs={c.faqs as Array<{ question: string; answer: string }>} />

      {/* Hero */}
      <section className="bg-background pt-24 pb-12 sm:pt-28 lg:pt-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav className="mb-6 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted">
            <Link href="/" className="transition-colors hover:text-primary">
              {c.breadcrumbHome}
            </Link>
            <span className="mx-2" aria-hidden>/</span>
            <span className="text-neutral-emphasis">{c.breadcrumbCurrent}</span>
          </nav>
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {c.eyebrow}
          </p>
          <h1 className="max-w-4xl font-display text-4xl font-bold leading-[1.12] tracking-tight text-neutral-emphasis md:text-5xl lg:text-6xl">
            {c.h1}
          </h1>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {c.heroLede}
            {c.heroPartnerLead}
            <Link href={getLocalizedPathname("/partners/anthropic-claude-partner/", locale)} className="text-primary underline underline-offset-2 hover:text-blue-800">
              {c.heroPartnerLink}
            </Link>
            {c.heroPartnerTail}
          </p>
        </div>
      </section>

      {/* The one-line answer callout */}
      <section className="bg-background pb-4">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-primary/30 bg-primary/5 p-6 sm:p-8">
            <p className="flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-primary">
              <ShieldCheck className="h-4 w-4" aria-hidden />
              {c.shortAnswerLabel}
            </p>
            <p className="mt-3 text-lg font-semibold text-neutral-emphasis">
              {c.shortAnswerQuestion}
            </p>
            <p className="mt-2 text-base leading-relaxed text-neutral-muted">
              {c.shortAnswerBodyBeforePersonal}
              <strong className="text-neutral-emphasis">{c.shortAnswerPersonal}</strong>
              {c.shortAnswerBetweenPersonalAndCompany}
              <strong className="text-neutral-emphasis">{c.shortAnswerCompany}</strong>
              {c.shortAnswerAfterCompany}
              <em>{c.shortAnswerCanEmphasis}</em>
              {c.shortAnswerAfterCan}
            </p>
          </div>
        </div>
      </section>

      {/* Comparison matrix */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.matrixEyebrow}
          </p>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-border text-neutral-emphasis">
                  <th className="py-3 pr-4 font-semibold" scope="col">&nbsp;</th>
                  <th className="px-3 py-3 font-semibold" scope="col">{c.matrixColIndividual}</th>
                  <th className="px-3 py-3 font-semibold" scope="col">{c.matrixColTeam}</th>
                  <th className="px-3 py-3 font-semibold" scope="col">{c.matrixColEnterprise}</th>
                </tr>
              </thead>
              <tbody>
                {c.matrix.map((section) => (
                  <Fragment key={section.group}>
                    <tr className="bg-background">
                      <th
                        scope="colgroup"
                        colSpan={4}
                        className="py-2 pr-4 font-mono text-xs uppercase tracking-[0.2em] text-neutral-muted"
                      >
                        {section.group}
                      </th>
                    </tr>
                    {section.rows.map((row) => (
                      <tr key={row[0]} className="border-b border-border/60 align-top">
                        <th scope="row" className="py-3 pr-4 font-normal text-neutral-emphasis">
                          {row[0]}
                        </th>
                        {row.slice(1).map((cell, i) => (
                          <td key={i} className="px-3 py-3 text-neutral-muted">
                            {cell}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-neutral-muted">
            {c.matrixFootnote}
          </p>
        </div>
      </section>

      {/* Plan-by-plan honest answer */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.planEyebrow}
          </p>
          <h2 className="mb-8 max-w-3xl font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {c.planHeading}
          </h2>
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {c.planPrivacy.map((card) => (
              <div
                key={card.title}
                className="flex h-full flex-col rounded-2xl border border-border bg-card p-6"
              >
                <p className="mb-2 font-mono text-xs uppercase tracking-[0.25em] text-neutral-muted">
                  {card.tag}
                </p>
                <h3 className="font-display text-lg font-semibold text-neutral-emphasis">
                  {card.title}
                </h3>
                <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{card.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Training + retention */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.trainingEyebrow}
          </p>
          <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {c.trainingHeading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-neutral-muted">
            {c.trainingParaOneBefore}
            <strong className="text-neutral-emphasis">{c.trainingParaOneEmphasis}</strong>
            {c.trainingParaOneAfter}
          </p>
          <p className="mt-4 text-base leading-relaxed text-neutral-muted">
            {c.trainingParaTwo}
          </p>
        </div>
      </section>

      {/* PDPA / Malaysia */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.pdpaEyebrow}
          </p>
          <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {c.pdpaHeading}
          </h2>
          <p className="mt-4 text-base leading-relaxed text-neutral-muted">
            {c.pdpaParaOne}
          </p>
          <p className="mt-4 text-base leading-relaxed text-neutral-muted">
            {c.pdpaParaTwoBefore}
            <strong className="text-neutral-emphasis">{c.pdpaParaTwoEmphasis}</strong>
            {c.pdpaParaTwoAfter}
          </p>
        </div>
      </section>

      {/* Internal link to the AI governance hub. */}
      <section className="bg-background px-4 py-10 sm:px-6 lg:px-8">
        <GovernanceCallout
          locale={locale}
          placement="claude_privacy"
          href="/ai-governance/ai-acceptable-use-policy-template/"
          cta="Read the free AI acceptable use policy template"
        />
      </section>

      {/* FAQ */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="mb-8 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.faqEyebrow}
          </p>
          <div className="space-y-4">
            {c.faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-border bg-card p-6"
              >
                <summary className="cursor-pointer list-none font-display text-base font-semibold text-neutral-emphasis marker:hidden">
                  {faq.question}
                </summary>
                <p className="mt-3 text-sm leading-relaxed text-neutral-muted">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Sources / citations */}
      <section className="bg-background py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <p className="mb-3 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.sourcesEyebrow}
          </p>
          <h2 className="font-display text-2xl font-bold text-neutral-emphasis md:text-3xl">
            {c.sourcesHeading}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-neutral-muted">
            {c.sourcesIntro}
          </p>
          <ul className="mt-4 space-y-3 text-sm text-neutral-muted">
            {c.sources.map((source) => (
              <li key={source.href} className="flex gap-2">
                <span className="text-primary" aria-hidden>·</span>
                <span>
                  <a href={source.href} className="text-primary underline hover:text-blue-800" target="_blank" rel="noopener noreferrer">{source.label}</a>{source.note}
                </span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:px-8">
          <div className="rounded-2xl border border-border bg-card p-6 text-sm text-neutral-muted">
            <p className="mb-2 font-mono text-xs uppercase tracking-[0.2em] text-neutral-emphasis">
              {c.disclaimerLabel}
            </p>
            <p>
              {c.disclaimerBeforeLink}
              <a
                href="https://trust.anthropic.com"
                className="text-primary underline hover:text-blue-800"
                target="_blank"
                rel="noopener noreferrer"
              >
                {c.disclaimerLinkText}
              </a>
              {c.disclaimerAfterLink}
            </p>
          </div>
        </div>
      </section>

      {/* Keep reading */}
      <section className="bg-neutral-surface py-16 lg:py-20">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <p className="mb-6 font-mono text-xs uppercase tracking-[0.3em] text-neutral-muted">
            {c.keepReadingLabel}
          </p>
          <div className="flex flex-wrap gap-x-8 gap-y-4">
            {c.readingLinks.map((link) => (
              <Link key={link.href} href={link.href} className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-blue-800">
                {link.label} <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <ConsultationCTA
        title={c.ctaTitle}
        description={c.ctaDescription}
        buttonText={c.ctaButtonText}
      />
    </div>
  );
}
